import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import Flipbook from "./components/Flipbook";
import PromoBanner from "./components/PromoBanner";
import {
  Snowflake,
  Wrench,
  AlertTriangle,
  Refrigerator,
  Warehouse,
  MapPin,
  Phone,
  Mail,
  Instagram,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Clock,
  Thermometer,
  Menu,
  X,
  Truck,
  CreditCard,
  BadgeCheck,
  Zap,
  Star,
  Quote,
  ExternalLink,
  Volume2,
  VolumeX,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVER = "/assets/jlc-cover.jpeg";
const FLEET = "/assets/jlc-fleet.jpeg";
const STOCK = "/assets/stock-cover.jpeg";
const LOGO = "/assets/jlc-logo-transparent.png";

const SERVICES = [
  {
    id: "installation",
    title: "Installation climatisation",
    kicker: "01 — Pose & mise en service",
    desc: "Installation de climatiseurs split, multi-split et gainables pour particuliers et professionnels. Étude thermique, dimensionnement et pose soignée.",
    icon: Snowflake,
  },
  {
    id: "maintenance",
    title: "Maintenance & entretien",
    kicker: "02 — Contrat annuel",
    desc: "Nettoyage, contrôle des pressions, recharge de gaz, vérification électrique. Contrats d'entretien préventif pour garder vos équipements au top.",
    icon: Wrench,
  },
  {
    id: "depannage",
    title: "Dépannage rapide",
    kicker: "03 — Intervention express",
    desc: "Panne, fuite, surchauffe ? Nous intervenons rapidement sur toute la Guyane pour remettre votre installation en service.",
    icon: AlertTriangle,
  },
  {
    id: "meubles",
    title: "Meubles frigorifiques",
    kicker: "04 — Froid commercial",
    desc: "Vitrines, armoires, bacs à glace, meubles de supermarché. Installation, entretien et réparation pour commerces et restaurants.",
    icon: Refrigerator,
  },
  {
    id: "chambre",
    title: "Chambres froides",
    kicker: "05 — Positive & négative",
    desc: "Conception, installation et maintenance de chambres froides positives et négatives pour hôtels et professionnels de l'agroalimentaire.",
    icon: Warehouse,
  },
  {
    id: "conseil",
    title: "Conseil & devis",
    kicker: "06 — Gratuit & sans engagement",
    desc: "Étude de votre projet, recommandations matériel et devis détaillé gratuits. Un seul interlocuteur pour toute la chaîne du froid.",
    icon: CheckCircle2,
  },
];

const SERVICE_OPTIONS = [
  "Installation climatisation",
  "Maintenance / Entretien",
  "Dépannage",
  "Meubles frigorifiques",
  "Chambre froide",
  "Autre",
];

const CITIES = [
  "Macouria",
  "Cayenne",
  "Kourou",
  "Matoury",
  "Rémire-Montjoly",
  "Saint-Laurent-du-Maroni",
  "Sinnamary",
  "Iracoubo",
  "Mana",
  "Roura",
  "Apatou",
  "Maripasoula",
];

// ---------- NAV ----------
const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: "Services" },
    { href: "#stock", label: "Stock" },
    { href: "#zone", label: "Zone" },
    { href: "#avis", label: "Avis" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="main-nav"
      style={{ top: "var(--promo-h, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-[color:var(--jlc-line)] shadow-[0_2px_30px_-10px_rgba(10,111,203,0.25)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-[110px]">
        <a
          href="#top"
          data-testid="logo-link"
          className="group relative flex items-center"
        >
          {/* Designer white band (only on transparent/hero) */}
          <span
            aria-hidden="true"
            className={`absolute inset-y-1 -inset-x-6 rounded-[2px] transition-all duration-500 pointer-events-none ${
              scrolled ? "opacity-0 scale-90" : "opacity-100 scale-100"
            } bg-gradient-to-r from-transparent via-white/95 to-transparent skew-y-[-3deg] shadow-[0_20px_60px_-15px_rgba(58,182,232,0.55)]`}
            style={{ clipPath: "polygon(4% 0, 96% 0, 100% 100%, 0 100%)" }}
          />
          {/* Thin cyan accent line under band */}
          <span
            aria-hidden="true"
            className={`absolute -bottom-0.5 left-4 right-4 h-[3px] rounded-full transition-all duration-500 pointer-events-none ${
              scrolled ? "opacity-0" : "opacity-100"
            } bg-gradient-to-r from-transparent via-[color:var(--jlc-cyan)] to-transparent`}
          />
          <img
            src={LOGO}
            alt="JLC Climatisation — La fraîcheur pour tous"
            className="relative h-20 sm:h-24 w-auto object-contain transition-all duration-500 group-hover:scale-[1.03] px-4 py-1.5"
          />
        </a>

        <nav className="hidden md:flex items-center gap-2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`relative text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                scrolled
                  ? "text-[color:var(--jlc-blue-ink)] hover:bg-[color:var(--jlc-frost)] hover:text-[color:var(--jlc-blue)]"
                  : "text-white/95 hover:bg-white/15 backdrop-blur-sm"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+594694432109"
            data-testid="nav-phone"
            className={`text-sm font-bold flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              scrolled
                ? "text-[color:var(--jlc-blue-deep)] hover:bg-[color:var(--jlc-frost)]"
                : "text-white hover:bg-white/15 backdrop-blur-sm"
            }`}
          >
            <Phone className="h-4 w-4" />
            0694 43 21 09
          </a>
          <a
            href="#contact"
            data-testid="nav-devis-button"
            className="btn-primary text-sm !rounded-full"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden p-2 rounded-full transition-colors ${
            scrolled
              ? "text-[color:var(--jlc-blue-deep)] bg-[color:var(--jlc-frost)]"
              : "text-white bg-white/15 backdrop-blur-sm"
          }`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-[color:var(--jlc-line)] bg-white"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col px-5 py-4 gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-semibold text-[color:var(--jlc-blue-ink)]"
              >
                {l.label}
              </a>
            ))}
            <a href="tel:+594694432109" className="btn-ghost justify-center">
              <Phone className="h-4 w-4" /> 0694 43 21 09
            </a>
            <a href="#contact" onClick={() => setOpen(false)} className="btn-primary justify-center">
              Demander un devis <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ---------- HERO ----------
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    style={{ paddingTop: "calc(110px + var(--promo-h, 0px))" }}
    className="relative min-h-[100vh] flex items-center overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={COVER}
        alt="Installation JLC Climatisation"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-overlay" />
    </div>

    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16 w-full">
      <div className="max-w-3xl text-white reveal">
        <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/15 backdrop-blur-md border border-white/25 rounded-full mb-8">
          <span className="h-2 w-2 rounded-full bg-[color:var(--jlc-cyan)] animate-pulse" />
          <span className="text-xs tracking-[0.22em] uppercase font-bold">
            Macouria · Toute la Guyane · 973
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-display font-black text-[46px] leading-[0.95] sm:text-6xl lg:text-[88px] lg:leading-[0.9] tracking-tighter"
        >
          L&apos;expertise du <span className="text-[color:var(--jlc-cyan)]">froid</span>
          <br />
          en Guyane.
        </h1>
        <p className="mt-7 max-w-xl text-[17px] lg:text-[19px] leading-relaxed text-white/90">
          JLC Climatisation installe, entretient et dépanne vos climatiseurs, meubles
          frigorifiques et chambres froides. Particuliers, restaurants, commerces — nous couvrons
          tout le 973.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a href="#contact" data-testid="hero-cta-devis" className="btn-primary">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="tel:+594694432109"
            data-testid="hero-cta-call"
            className="btn-ghost bg-white/10 border-white text-white hover:bg-white hover:text-[color:var(--jlc-blue-ink)]"
          >
            <Phone className="h-4 w-4" /> 0694 43 21 09
          </a>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
          {[
            { k: "10+", v: "Années d'expérience" },
            { k: "24/7", v: "Dépannage rapide" },
            { k: "973", v: "Toute la Guyane" },
          ].map((s) => (
            <div key={s.k} className="border-t-2 border-[color:var(--jlc-cyan)] pt-3">
              <div className="font-display font-black text-3xl lg:text-5xl text-white">{s.k}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 mt-1 font-semibold">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* floating badge bottom right */}
    <div className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 bg-white px-5 py-4 rounded-md shadow-2xl border border-[color:var(--jlc-line)]">
      <div className="h-10 w-10 bg-[color:var(--jlc-frost)] grid place-items-center rounded-full">
        <ShieldCheck className="h-5 w-5 text-[color:var(--jlc-blue)]" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--jlc-blue)]">
          Techniciens qualifiés
        </div>
        <div className="text-sm font-semibold text-[color:var(--jlc-blue-ink)]">
          Froid positif & négatif
        </div>
      </div>
    </div>

    {/* city marquee */}
    <div className="absolute bottom-0 inset-x-0 bg-white/10 backdrop-blur-sm border-t border-white/20 overflow-hidden">
      <div className="marquee-track flex whitespace-nowrap py-3">
        {Array(2)
          .fill(0)
          .map((_, i) => (
            <div key={i} className="flex gap-10 px-5">
              {CITIES.concat(["Saint-Georges", "Régina"]).map((c) => (
                <span
                  key={c + i}
                  className="font-display font-bold text-base uppercase tracking-wider text-white/80 flex items-center gap-6"
                >
                  {c} <span className="text-[color:var(--jlc-cyan)]">❄</span>
                </span>
              ))}
            </div>
          ))}
      </div>
    </div>
  </section>
);

// ---------- SERVICES ----------
const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative bg-[color:var(--jlc-bg-soft)] py-20 lg:py-32"
  >
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-[2px] w-10 bg-[color:var(--jlc-blue)]" />
            <span className="label-kicker">Nos services</span>
          </div>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95] text-[color:var(--jlc-blue-ink)]">
            Une expertise
            <br />
            <span className="text-[color:var(--jlc-blue)]">complète du froid.</span>
          </h2>
        </div>
        <p className="max-w-sm text-[color:var(--jlc-ink-soft)] text-[15px] leading-relaxed">
          Du climatiseur résidentiel à la chambre froide industrielle, nous intervenons sur
          l&apos;ensemble de vos installations thermiques.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className="card-soft group relative p-8 lg:p-10 flex flex-col"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="flex items-center justify-between mb-7">
                <span className="label-kicker">{s.kicker}</span>
                <div className="h-12 w-12 bg-[color:var(--jlc-frost)] grid place-items-center rounded-full group-hover:bg-[color:var(--jlc-blue)] transition-colors">
                  <Icon className="h-5 w-5 text-[color:var(--jlc-blue)] group-hover:text-white transition-colors" />
                </div>
              </div>
              <h3 className="font-display font-black text-2xl lg:text-[26px] tracking-tight leading-tight mb-3 text-[color:var(--jlc-blue-ink)]">
                {s.title}
              </h3>
              <p className="text-[color:var(--jlc-ink-soft)] text-[14.5px] leading-relaxed flex-1">
                {s.desc}
              </p>
              <a
                href="#contact"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[color:var(--jlc-blue)] w-fit group-hover:gap-3 transition-all"
              >
                Demander une intervention <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------- STOCK ----------
const Stock = () => {
  const badges = [
    { icon: BadgeCheck, text: "Produits neufs sous garantie" },
    { icon: Snowflake, text: "Split · Multi-split · Gainable" },
    { icon: Zap, text: "Classe énergétique A à A+++" },
  ];
  return (
    <section
      id="stock"
      data-testid="stock-section"
      className="relative bg-gradient-to-br from-[color:var(--jlc-blue-ink)] via-[color:var(--jlc-blue-deep)] to-[color:var(--jlc-blue)] text-white overflow-hidden py-20 lg:py-28"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-40 -left-20 h-[500px] w-[500px] rounded-full bg-[color:var(--jlc-cyan)] opacity-15 blur-3xl" />
      <div className="absolute -bottom-40 -right-20 h-[500px] w-[500px] rounded-full bg-white opacity-[0.08] blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6">
            <span className="h-2 w-2 rounded-full bg-[color:var(--jlc-cyan)] animate-pulse" />
            <span className="text-xs tracking-[0.22em] uppercase font-bold">
              En stock à Macouria
            </span>
          </div>
          <h2 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            Climatiseurs
            <br />
            <span className="text-[color:var(--jlc-cyan)]">disponibles</span>
            <br />
            maintenant.
          </h2>
          <p className="mt-6 text-lg lg:text-xl text-white/85 max-w-xl leading-relaxed">
            Confort et fraîcheur garantis toute l&apos;année. Un large choix de climatiseurs neufs en
            stock dans notre dépôt de Macouria — livraison et pose rapides sur toute la Guyane.
          </p>

          <div className="mt-8 space-y-3">
            {badges.map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-3 text-[15px] text-white/90"
              >
                <div className="h-9 w-9 rounded-full bg-white/10 border border-white/20 grid place-items-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-[color:var(--jlc-cyan)]" />
                </div>
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#contact" data-testid="stock-cta-quote" className="btn-primary bg-white !text-[color:var(--jlc-blue-deep)] hover:!bg-[color:var(--jlc-cyan)] hover:!text-white">
              Demander un modèle <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="tel:+594694432109"
              className="btn-ghost bg-white/10 border-white/40 text-white hover:bg-white hover:text-[color:var(--jlc-blue-ink)]"
            >
              <Phone className="h-4 w-4" /> Nous appeler
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <Flipbook />

          {/* Payment + EDF partner card */}
          <div className="mt-6 grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div className="bg-white text-[color:var(--jlc-blue-ink)] p-4 rounded-xl flex items-start gap-3 shadow-xl">
              <CreditCard className="h-6 w-6 text-[color:var(--jlc-blue)] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--jlc-blue)] font-bold">
                  Paiement
                </div>
                <div className="text-sm font-bold leading-tight mt-0.5">
                  4× sans frais
                </div>
                <div className="text-[11px] text-[color:var(--jlc-ink-soft)] leading-snug">
                  VISA · MasterCard · CB
                </div>
              </div>
            </div>
            <div className="bg-[color:var(--jlc-cyan)] text-white p-4 rounded-xl flex items-start gap-3 shadow-xl">
              <BadgeCheck className="h-6 w-6 text-white flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-white/85 font-bold">
                  Partenaire
                </div>
                <div className="text-sm font-bold leading-tight mt-0.5">AGIR PLUS</div>
                <div className="text-[11px] text-white/85 leading-snug">d&apos;EDF · primes éco</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- SUMMER PROMO ----------
const WorldCupPromo = () => {
  const videoRef = React.useRef(null);
  const [muted, setMuted] = React.useState(true);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  };

  return (
    <section
      id="coupe-monde"
      data-testid="worldcup-section"
      className="relative bg-gradient-to-br from-[color:var(--jlc-blue-ink)] via-[#03285a] to-[color:var(--jlc-blue-deep)] text-white py-24 lg:py-32 overflow-hidden"
    >
      {/* Sun ray backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 85% 20%, rgba(251,191,36,0.28) 0%, rgba(251,191,36,0.08) 25%, transparent 55%)",
        }}
      />
      {/* Cyan glow */}
      <div className="absolute -bottom-40 -left-20 h-[600px] w-[600px] rounded-full bg-[color:var(--jlc-cyan)] opacity-15 blur-3xl" />
      {/* Emerald accent */}
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-emerald-400 opacity-10 blur-3xl" />

      {/* Sun rays (decorative) */}
      <svg
        className="absolute top-8 right-8 h-40 w-40 opacity-20 hidden lg:block"
        viewBox="0 0 200 200"
        aria-hidden="true"
      >
        <g stroke="#FBBF24" strokeWidth="2" strokeLinecap="round">
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            const x1 = 100 + Math.cos(angle) * 55;
            const y1 = 100 + Math.sin(angle) * 55;
            const x2 = 100 + Math.cos(angle) * 90;
            const y2 = 100 + Math.sin(angle) * 90;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
        </g>
        <circle cx="100" cy="100" r="35" fill="#FBBF24" opacity="0.4" />
        <circle cx="100" cy="100" r="20" fill="#FBBF24" opacity="0.9" />
      </svg>

      {/* Palm decorative shapes */}
      <div className="absolute -bottom-4 left-4 text-6xl opacity-20 rotate-[-20deg] hidden md:block">🌴</div>
      <div className="absolute top-1/2 right-4 text-5xl opacity-15 rotate-[15deg] hidden md:block">🌴</div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: Text content */}
        <div className="lg:col-span-6 reveal">
          {/* Badge */}
          <div className="inline-flex items-center gap-3 pl-2 pr-5 py-2 bg-gradient-to-r from-amber-400/30 to-emerald-400/20 backdrop-blur-md border border-amber-300/40 rounded-full mb-7 shadow-[0_10px_30px_-10px_rgba(251,191,36,0.5)]">
            <span className="h-7 w-7 rounded-full bg-amber-400 grid place-items-center text-base shadow-inner">
              ☀️
            </span>
            <span className="text-[11px] tracking-[0.28em] uppercase font-black text-amber-100">
              Offre d&apos;été · édition 2026
            </span>
          </div>

          {/* Title with decorative underline */}
          <h2 className="font-display font-black text-5xl lg:text-[80px] tracking-tighter leading-[0.9]">
            Un été
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-br from-[color:var(--jlc-cyan)] via-sky-300 to-white bg-clip-text text-transparent">
                au frais
              </span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 300 12"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M0,6 Q75,0 150,6 T300,6"
                  stroke="url(#cyanGrad)"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="cyanGrad" x1="0" x2="1">
                    <stop offset="0%" stopColor="#3AB6E8" stopOpacity="0" />
                    <stop offset="50%" stopColor="#3AB6E8" stopOpacity="1" />
                    <stop offset="100%" stopColor="#3AB6E8" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </span>{" "}
            <span className="text-3xl lg:text-5xl">❄</span>
          </h2>

          <p className="mt-7 text-lg lg:text-xl text-white/85 leading-relaxed max-w-xl">
            L&apos;été guyanais est chaud, mais chez vous restez au frais ❄️🥶
          </p>

          {/* Feature list with colored bullets */}
          <div className="mt-9 space-y-3">
            {[
              {
                dot: "bg-emerald-400",
                text: "Climatisation Midea Xtreme Save",
                sub: "9000 BTU · A+++ · Silencieuse",
              },
              {
                dot: "bg-[color:var(--jlc-cyan)]",
                text: "Installation professionnelle incluse",
                sub: "Par nos techniciens certifiés",
              },
              {
                dot: "bg-amber-400",
                text: "Confort garanti tout l'été",
                sub: "Sous garantie constructeur",
              },
            ].map((item) => (
              <div
                key={item.text}
                className="group flex items-center gap-4 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl px-5 py-3.5 hover:bg-white/[0.1] hover:border-white/20 transition-all"
              >
                <span
                  className={`h-9 w-9 rounded-full ${item.dot} grid place-items-center flex-shrink-0 shadow-lg`}
                >
                  <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className="flex-1">
                  <div className="font-bold text-[15px] text-white leading-tight">
                    {item.text}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Price highlight */}
          <div className="mt-8 relative inline-flex items-center gap-5 pl-3 pr-6 py-3 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl shadow-[0_20px_50px_-15px_rgba(251,191,36,0.55)] text-[color:var(--jlc-blue-ink)]">
            <div className="h-14 w-14 rounded-xl bg-white grid place-items-center flex-shrink-0">
              <span className="text-3xl">💳</span>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-black opacity-70">
                4× sans frais par CB
              </div>
              <div className="font-display font-black text-3xl leading-none mt-1">
                150€<span className="text-lg font-bold opacity-70"> / mois</span>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="#devis-form"
              data-testid="worldcup-cta-quote"
              onClick={() => {
                try {
                  sessionStorage.setItem("preselectService", "Installation climatisation");
                  sessionStorage.setItem(
                    "preselectMessage",
                    "Bonjour, je suis intéressé(e) par votre offre d'été Midea Xtreme Save (9000 BTU, 4× sans frais). Merci de me recontacter."
                  );
                  window.dispatchEvent(new Event("jlc:prefill"));
                } catch (e) {
                  /* ignore storage errors */
                }
              }}
              className="btn-primary bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 !text-white !rounded-full px-7"
            >
              Je veux ma clim Midea
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="tel:+594694432109"
              className="btn-ghost bg-white/10 border-white/40 text-white hover:bg-white hover:text-[color:var(--jlc-blue-ink)] !rounded-full"
            >
              <Phone className="h-4 w-4" /> 0694 43 21 09
            </a>
          </div>

          <p className="mt-5 text-sm text-white/60 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Installation rapide — Profitez de la fraîcheur cet été !
          </p>
        </div>

        {/* Right: Video with premium framing */}
        <div className="lg:col-span-6 relative">
          <div className="relative max-w-md mx-auto">
            {/* Glow behind video */}
            <div className="absolute -inset-6 bg-gradient-to-br from-[color:var(--jlc-cyan)]/40 via-amber-300/20 to-emerald-400/30 rounded-[2.5rem] blur-2xl opacity-70" />

            {/* Video card */}
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.7)] ring-4 ring-white/15 transform hover:scale-[1.02] transition-transform duration-500">
              <video
                ref={videoRef}
                data-testid="worldcup-video"
                src="/assets/coupe-monde-2026.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-contain bg-black"
              />

              {/* Sound toggle */}
              <button
                onClick={toggleMute}
                data-testid="worldcup-video-mute"
                aria-label={muted ? "Activer le son" : "Couper le son"}
                className="absolute bottom-4 right-4 h-11 w-11 grid place-items-center bg-black/60 backdrop-blur-md hover:bg-black/80 text-white rounded-full transition-all"
              >
                {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>

              {/* Live badge */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-red-500 rounded-full shadow-lg">
                <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                <span className="text-[10px] font-black tracking-wider uppercase text-white">
                  Promo été 2026
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- ZONE ----------
const Zone = () => (
  <section id="zone" data-testid="zone-section" className="relative bg-white py-20 lg:py-32">
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-6 relative">
        <div className="aspect-[4/3] overflow-hidden rounded-lg border border-[color:var(--jlc-line)] shadow-[0_30px_60px_-20px_rgba(10,111,203,0.25)]">
          <img src={FLEET} alt="Flotte JLC Climatisation" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-5 -right-5 bg-[color:var(--jlc-blue)] text-white p-6 max-w-[260px] hidden md:block rounded-md shadow-xl">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/80 font-bold">
            Base opérationnelle
          </div>
          <div className="font-display font-black text-2xl mt-1">Macouria — 97355</div>
          <div className="text-sm opacity-90 mt-1">Flotte mobile sur tout le 973</div>
        </div>
        <div className="absolute -top-4 -left-4 bg-white px-4 py-3 border border-[color:var(--jlc-line)] rounded-md shadow-lg hidden md:flex items-center gap-2">
          <Truck className="h-4 w-4 text-[color:var(--jlc-blue)]" />
          <span className="text-xs font-bold text-[color:var(--jlc-blue-ink)]">
            Équipe mobile équipée
          </span>
        </div>
      </div>

      <div className="lg:col-span-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-[2px] w-10 bg-[color:var(--jlc-blue)]" />
          <span className="label-kicker">Zone d&apos;intervention</span>
        </div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-[color:var(--jlc-blue-ink)]">
          De Cayenne à Saint-Laurent.
          <br />
          <span className="text-[color:var(--jlc-blue)]">Partout où le froid compte.</span>
        </h2>
        <p className="mt-5 text-[color:var(--jlc-ink-soft)] text-[15.5px] leading-relaxed max-w-xl">
          Basés au 2 Rue Papaati — Soula, 97355 Macouria, nous nous déplaçons sur l&apos;ensemble de la Guyane :
          littoral, intérieur, entreprises et particuliers. Prise en charge rapide, peu importe
          votre commune.
        </p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CITIES.map((c) => (
            <div
              key={c}
              className="bg-[color:var(--jlc-frost)] border border-[color:var(--jlc-line)] px-4 py-3 flex items-center gap-2 text-sm font-semibold text-[color:var(--jlc-blue-ink)] rounded-md"
            >
              <MapPin className="h-3.5 w-3.5 text-[color:var(--jlc-blue)]" />
              {c}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- TESTIMONIALS / GOOGLE REVIEWS ----------
const REVIEWS = [
  {
    name: "Alain Bulin",
    rating: 5,
    date: "il y a 3 heures",
    visited: "Août 2026",
    text: "Rapide. Efficace. Professionnel.",
    fresh: true,
  },
  {
    name: "Christelle Demba",
    rating: 5,
    date: "il y a 7 mois",
    visited: "Septembre 2025",
    text: "Une très bonne équipe, de l'accueil aux techniciens, sérieux, ponctuels, merci",
  },
  {
    name: "Sandrine Chardin",
    rating: 5,
    date: "il y a 2 ans",
    visited: "Novembre 2023",
    text: "Excellent service. Nous avons effectué l'inspection/nettoyage de nos thermopompes. Service efficace et courtois à un prix très compétitif. Je recommande cette compagnie.",
  },
  {
    name: "Laëny Gromat",
    rating: 5,
    date: "il y a 7 mois",
    visited: "Septembre 2025",
    text: "Très professionnel et rapide",
  },
  {
    name: "Lu Lu",
    rating: 5,
    date: "il y a 7 mois",
    visited: "Août 2025",
    text: "Très bon travail",
  },
  {
    name: "Jérémy Yhap",
    rating: 5,
    date: "il y a 4 ans",
    visited: "Juin 2021",
    text: "Très satisfait de la main-d'œuvre",
  },
];

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?q=avis+sur+j.l.c+climatisation+macouria&tbm=lcl";

const GOOGLE_LEAVE_REVIEW_URL =
  "https://www.google.com/maps?cid=14461932548815743849";

const AVATAR_COLORS = [
  "bg-[color:var(--jlc-blue)]",
  "bg-[color:var(--jlc-cyan)]",
  "bg-[color:var(--jlc-blue-deep)]",
  "bg-indigo-500",
  "bg-sky-600",
];

const initialsOf = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Testimonials = () => {
  const [featuredIdx, setFeaturedIdx] = React.useState(0);

  // Auto-rotate featured review every 6s
  React.useEffect(() => {
    const id = setInterval(() => {
      setFeaturedIdx((i) => (i + 1) % REVIEWS.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  const featured = REVIEWS[featuredIdx];
  // Double the list for seamless marquee
  const marqueeList = [...REVIEWS, ...REVIEWS, ...REVIEWS];

  return (
    <section
      id="avis"
      data-testid="testimonials-section"
      className="relative bg-white py-20 lg:py-28 overflow-hidden"
    >
      {/* Subtle radial background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(185,227,246,0.6)_0%,transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(234,245,252,1)_0%,transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        {/* Header row: title + Google card */}
        <div className="grid lg:grid-cols-12 gap-8 items-center mb-16">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="h-[2px] w-10 bg-[color:var(--jlc-blue)]" />
              <span className="label-kicker">Ils parlent de nous</span>
            </div>
            <h2 className="font-display font-black text-4xl lg:text-[64px] tracking-tighter leading-[0.95] text-[color:var(--jlc-blue-ink)]">
              5 étoiles en moyenne,
              <br />
              100% <span className="text-[color:var(--jlc-blue)]">vérifiés</span>.
            </h2>
            <p className="mt-5 text-[color:var(--jlc-ink-soft)] text-[16px] leading-relaxed max-w-xl">
              Des avis authentiques laissés par nos clients sur Google. Notre meilleure preuve de
              sérieux — passez votre souris pour pauser le défilement.
            </p>
          </div>

          {/* Google rating mega card */}
          <div className="lg:col-span-5">
            <div className="relative bg-white rounded-3xl p-6 shadow-[0_30px_80px_-30px_rgba(10,111,203,0.35)] border border-[color:var(--jlc-line)] overflow-hidden">
              <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-[color:var(--jlc-frost)] opacity-70" />
              <div className="relative flex items-center gap-4">
                <div className="h-16 w-16 bg-white rounded-2xl grid place-items-center flex-shrink-0 ring-1 ring-[color:var(--jlc-line)]">
                  <svg width="34" height="34" viewBox="0 0 48 48">
                    <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-ink-soft)] font-bold">
                    Note Google
                  </div>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="font-display font-black text-4xl text-[color:var(--jlc-blue-ink)] leading-none">
                      4,5
                    </span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${
                            i <= 4
                              ? "fill-[#FBBC05] text-[#FBBC05]"
                              : "fill-[#FBBC05]/50 text-[#FBBC05]/50"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-[color:var(--jlc-ink-soft)] mt-1">
                    7 avis vérifiés · Guyane française
                  </div>
                </div>
                <a
                  href={GOOGLE_LEAVE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-google-review"
                  className="btn-primary text-sm flex-shrink-0 !rounded-full"
                >
                  <Star className="h-3.5 w-3.5 fill-current" />
                  Avis
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Featured rotating testimonial */}
        <div className="relative mb-10">
          <div className="relative bg-gradient-to-br from-[color:var(--jlc-blue-ink)] via-[color:var(--jlc-blue-deep)] to-[color:var(--jlc-blue)] rounded-3xl p-8 lg:p-14 overflow-hidden text-white shadow-[0_40px_80px_-30px_rgba(0,31,77,0.55)]">
            <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-[color:var(--jlc-cyan)] opacity-20 blur-3xl" />
            <Quote className="absolute top-8 left-8 h-20 w-20 text-white/10" />

            <div
              key={featuredIdx}
              className="relative max-w-4xl animate-[fadeUp_0.7s_ease_forwards]"
            >
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-5 w-5 fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>
                {featured.fresh && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 text-white text-[10px] tracking-[0.22em] uppercase font-black rounded-full shadow-lg">
                    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                    Nouveau
                  </span>
                )}
              </div>
              <p className="font-display font-bold text-2xl lg:text-4xl leading-tight tracking-tight">
                « {featured.text} »
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div
                  className={`h-12 w-12 rounded-full ${AVATAR_COLORS[featuredIdx % AVATAR_COLORS.length]} ring-4 ring-white/20 grid place-items-center text-white font-black text-sm flex-shrink-0`}
                >
                  {initialsOf(featured.name)}
                </div>
                <div>
                  <div className="font-bold text-lg">{featured.name}</div>
                  <div className="text-white/70 text-sm">
                    Client JLC · {featured.date} · Visite {featured.visited}
                  </div>
                </div>
              </div>
            </div>

            {/* Dots */}
            <div className="relative mt-8 flex items-center gap-2">
              {REVIEWS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setFeaturedIdx(i)}
                  aria-label={`Voir l'avis de ${REVIEWS[i].name}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === featuredIdx
                      ? "w-10 bg-[color:var(--jlc-cyan)]"
                      : "w-1.5 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
              <span className="ml-3 text-xs text-white/50 font-mono tracking-wider">
                {String(featuredIdx + 1).padStart(2, "0")} / {String(REVIEWS.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>

        {/* Wall of Love — infinite marquee */}
        <div className="relative group">
          {/* Edge fade masks */}
          <div className="absolute inset-y-0 left-0 w-20 z-10 bg-gradient-to-r from-white to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-20 z-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />

          <div className="flex gap-5 overflow-hidden py-2">
            <div className="flex gap-5 marquee-track group-hover:[animation-play-state:paused]">
              {marqueeList.map((r, i) => (
                <div
                  key={`mar-${i}`}
                  data-testid={`marquee-review-${i}`}
                  className="flex-shrink-0 w-[340px] bg-white border border-[color:var(--jlc-line)] rounded-2xl p-5 hover:border-[color:var(--jlc-blue)] hover:shadow-[0_20px_40px_-20px_rgba(10,111,203,0.35)] transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`h-3.5 w-3.5 ${
                            s <= r.rating
                              ? "fill-[#FBBC05] text-[#FBBC05]"
                              : "text-zinc-200"
                          }`}
                        />
                      ))}
                    </div>
                    <svg width="14" height="14" viewBox="0 0 48 48">
                      <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                  </div>
                  <p className="text-[14px] leading-relaxed text-[color:var(--jlc-blue-ink)] font-medium mb-4 line-clamp-4 h-[84px]">
                    « {r.text} »
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-[color:var(--jlc-line)]">
                    <div
                      className={`h-9 w-9 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} grid place-items-center text-white font-bold text-xs flex-shrink-0`}
                    >
                      {initialsOf(r.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[13px] text-[color:var(--jlc-blue-ink)] truncate">
                        {r.name}
                      </div>
                      <div className="text-[10px] text-[color:var(--jlc-ink-soft)]">
                        {r.date}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA line */}
        <div className="mt-12 text-center">
          <p className="text-[color:var(--jlc-ink-soft)] text-sm mb-5">
            Vous avez fait appel à JLC Climatisation ? Votre avis nous aide à grandir.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-google-reviews"
              className="btn-ghost"
            >
              Voir tous les avis Google
              <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href={GOOGLE_LEAVE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              <Star className="h-4 w-4 fill-current" />
              Laissez-nous un avis
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- ABOUT ----------
const About = () => {
  const pillars = [
    {
      icon: ShieldCheck,
      title: "Techniciens certifiés",
      desc: "Équipe formée et habilitée sur les fluides frigorigènes et les équipements professionnels.",
    },
    {
      icon: Clock,
      title: "Réactivité 973",
      desc: "Dépannage rapide sur toute la Guyane, interventions planifiées sous les meilleurs délais.",
    },
    {
      icon: CheckCircle2,
      title: "Devis transparent",
      desc: "Estimations claires, sans surprise. Tarifs adaptés aux particuliers comme aux pros.",
    },
  ];
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[color:var(--jlc-frost)] py-20 lg:py-32 overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[color:var(--jlc-sky)] opacity-50 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-[2px] w-10 bg-[color:var(--jlc-blue)]" />
            <span className="label-kicker">À propos</span>
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-[color:var(--jlc-blue-ink)]">
            Un partenaire fiable,
            <br />
            <span className="text-[color:var(--jlc-blue)]">près de chez vous.</span>
          </h2>
          <p className="mt-5 text-[color:var(--jlc-ink-soft)] text-[15.5px] leading-relaxed">
            JLC Climatisation accompagne particuliers et professionnels guyanais dans
            l&apos;installation et la maintenance de leurs équipements thermiques et frigorifiques. Notre
            engagement : un travail soigné, un service humain, et des solutions durables adaptées
            au climat tropical.
          </p>
          <a href="#contact" className="btn-primary mt-8">
            Parlons de votre projet <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${p.title}`}
                className="card-soft p-8 lg:p-10 bg-white"
              >
                <div className="h-12 w-12 rounded-full bg-[color:var(--jlc-frost)] grid place-items-center">
                  <Icon className="h-6 w-6 text-[color:var(--jlc-blue)]" strokeWidth={2} />
                </div>
                <h3 className="font-display font-bold text-xl mt-5 text-[color:var(--jlc-blue-ink)]">
                  {p.title}
                </h3>
                <p className="text-sm text-[color:var(--jlc-ink-soft)] mt-2 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            );
          })}
          <div className="bg-[color:var(--jlc-blue)] text-white p-8 lg:p-10 sm:col-span-2 rounded-lg relative overflow-hidden">
            <Thermometer className="absolute -right-6 -bottom-6 h-40 w-40 text-white/10" />
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/80 relative">
              Engagement
            </div>
            <p className="font-display font-black text-2xl lg:text-3xl mt-3 leading-tight relative">
              Chaque intervention est réalisée avec rigueur, propreté et respect du matériel.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- CONTACT ----------
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  // Prefill from Midea CTA
  useEffect(() => {
    const applyPrefill = () => {
      try {
        const s = sessionStorage.getItem("preselectService");
        const m = sessionStorage.getItem("preselectMessage");
        if (s || m) {
          setForm((f) => ({
            ...f,
            service: s || f.service,
            message: m || f.message,
          }));
          sessionStorage.removeItem("preselectService");
          sessionStorage.removeItem("preselectMessage");
        }
      } catch (e) {
        /* ignore storage errors */
      }
    };
    applyPrefill();
    window.addEventListener("jlc:prefill", applyPrefill);
    return () => window.removeEventListener("jlc:prefill", applyPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", city: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail?.[0]?.msg || "Erreur lors de l'envoi. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-[2px] w-10 bg-[color:var(--jlc-blue)]" />
            <span className="label-kicker">Contact · Devis</span>
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-[color:var(--jlc-blue-ink)]">
            Parlons de
            <br />
            <span className="text-[color:var(--jlc-blue)]">votre projet.</span>
          </h2>
          <p className="mt-5 text-[color:var(--jlc-ink-soft)] text-[15.5px] leading-relaxed max-w-md">
            Remplissez le formulaire ou contactez-nous directement. Nous répondons sous 24h
            ouvrées.
          </p>

          <div className="mt-8 space-y-4">
            {[
              {
                icon: Phone,
                label: "Téléphone",
                value: "0694 43 21 09",
                href: "tel:+594694432109",
                testid: "contact-phone-link",
              },
              {
                icon: Mail,
                label: "Email",
                value: "jlcclim973@gmail.com",
                href: "mailto:jlcclim973@gmail.com",
                testid: "contact-email-link",
              },
              {
                icon: Instagram,
                label: "Instagram",
                value: "@jlc_climatisation973",
                href: "https://instagram.com/jlc_climatisation973",
                testid: "contact-instagram-link",
                external: true,
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={item.testid}
                  {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="flex items-center gap-4 group"
                >
                  <div className="h-12 w-12 bg-[color:var(--jlc-frost)] border border-[color:var(--jlc-line)] grid place-items-center rounded-full group-hover:bg-[color:var(--jlc-blue)] transition-colors">
                    <Icon className="h-5 w-5 text-[color:var(--jlc-blue)] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold">
                      {item.label}
                    </div>
                    <div className="font-display font-bold text-lg text-[color:var(--jlc-blue-ink)] break-all">
                      {item.value}
                    </div>
                  </div>
                </a>
              );
            })}
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-[color:var(--jlc-frost)] border border-[color:var(--jlc-line)] grid place-items-center rounded-full">
                <MapPin className="h-5 w-5 text-[color:var(--jlc-blue)]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold">
                  Adresse
                </div>
                <div className="font-display font-bold text-lg text-[color:var(--jlc-blue-ink)] leading-tight">
                  2 Rue Papaati — Soula
                </div>
                <div className="text-sm text-[color:var(--jlc-ink-soft)]">
                  97355 Macouria · Guyane
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          id="devis-form"
          className="lg:col-span-7 bg-[color:var(--jlc-bg-soft)] p-6 lg:p-10 rounded-lg border border-[color:var(--jlc-line)] scroll-mt-32"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Nom complet *
              </label>
              <input
                data-testid="contact-input-name"
                required
                className="jlc-input"
                value={form.name}
                onChange={update("name")}
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Téléphone *
              </label>
              <input
                data-testid="contact-input-phone"
                required
                className="jlc-input"
                value={form.phone}
                onChange={update("phone")}
                placeholder="0694 00 00 00"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Email *
              </label>
              <input
                data-testid="contact-input-email"
                type="email"
                required
                className="jlc-input"
                value={form.email}
                onChange={update("email")}
                placeholder="vous@exemple.com"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Commune
              </label>
              <input
                data-testid="contact-input-city"
                className="jlc-input"
                value={form.city}
                onChange={update("city")}
                placeholder="Macouria, Cayenne…"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Service souhaité *
              </label>
              <select
                data-testid="contact-input-service"
                className="jlc-input"
                value={form.service}
                onChange={update("service")}
              >
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-blue)] font-bold block mb-2">
                Votre message *
              </label>
              <textarea
                data-testid="contact-input-message"
                required
                rows={5}
                className="jlc-input resize-none"
                value={form.message}
                onChange={update("message")}
                placeholder="Décrivez votre besoin : type d'installation, surface, contraintes, etc."
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[color:var(--jlc-ink-soft)] max-w-sm">
              En envoyant ce formulaire, vous acceptez d&apos;être recontacté par JLC Climatisation au
              sujet de votre demande.
            </p>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Envoi..." : "Envoyer ma demande"}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

// ---------- FOOTER ----------
const Footer = () => (
  <footer
    data-testid="footer"
    className="bg-[color:var(--jlc-blue-ink)] text-white relative overflow-hidden"
  >
    <div className="absolute -top-32 -right-32 h-[400px] w-[400px] rounded-full bg-[color:var(--jlc-blue)] opacity-20 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="relative inline-block mb-6">
          <div className="absolute -inset-4 bg-[radial-gradient(circle_at_center,rgba(58,182,232,0.45)_0%,transparent_70%)] blur-2xl rounded-full" />
          <img
            src={LOGO}
            alt="JLC Climatisation"
            className="relative h-16 w-auto object-contain [filter:brightness(0)_invert(1)_drop-shadow(0_4px_20px_rgba(58,182,232,0.6))]"
          />
        </div>
        <p className="text-sm text-white/70 max-w-md leading-relaxed">
          Installation, maintenance et dépannage de climatiseurs, meubles frigorifiques et chambres
          froides. Basés à Macouria, nous intervenons sur toute la Guyane.
        </p>
        <div className="mt-5 text-sm text-white/80 not-italic flex items-start gap-2">
          <MapPin className="h-4 w-4 mt-0.5 text-[color:var(--jlc-cyan)]" />
          <div>
            <div>2 Rue Papaati — Soula</div>
            <div>97355 Macouria — Guyane</div>
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-cyan)] font-bold mb-4">
          Services
        </div>
        <ul className="space-y-2 text-sm text-white/80">
          <li>Installation climatisation</li>
          <li>Maintenance & entretien</li>
          <li>Dépannage</li>
          <li>Meubles frigorifiques</li>
          <li>Chambres froides</li>
        </ul>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--jlc-cyan)] font-bold mb-4">
          Contact
        </div>
        <ul className="space-y-2 text-sm text-white/80">
          <li>
            <a href="tel:+594694432109" className="hover:text-[color:var(--jlc-cyan)]">
              0694 43 21 09
            </a>
          </li>
          <li>
            <a
              href="mailto:jlcclim973@gmail.com"
              className="hover:text-[color:var(--jlc-cyan)] break-all"
            >
              jlcclim973@gmail.com
            </a>
          </li>
          <li>
            <a
              href="https://instagram.com/jlc_climatisation973"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[color:var(--jlc-cyan)]"
            >
              @jlc_climatisation973
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-white/60">
        <div>© {new Date().getFullYear()} JLC Climatisation. Tous droits réservés.</div>
        <div>Guyane française · 973</div>
      </div>
    </div>
  </footer>
);

// ---------- APP ----------
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <PromoBanner />
      <Nav />
      <main>
        <Hero />
        <Services />
        <Stock />
        <WorldCupPromo />
        <Zone />
        <Testimonials />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
